import { computed, makeObservable } from 'mobx'

import { IProject } from '~/client/graph'
import mobileRoutes from '~/client/src/mobile/constants/mobileRoutes'
import SectionNames from '~/client/src/mobile/enums/SectionNames'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import {
  GET_PROJECT_MEMBERS,
  LOGOUT,
  SHOW_ONE_TIME_POPUP,
} from '~/client/src/shared/stores/EventStore/eventConstants'

import MobileEventStore from '../../stores/EventStore/MobileEvents.store'
import MobileInitialState from '../../stores/MobileInitialState'
import MobileCommonStore from '../../stores/ui/MobileCommon.store'

interface ISidebarSection {
  id: SectionNames
  route: string
  isDisabled?: boolean
}

export interface ISidebarItem extends ISidebarSection {
  name: string
}

export default class SidebarStore {
  private readonly sidebarSections: ISidebarSection[] = [
    {
      id: SectionNames.PROFILE,
      route: mobileRoutes.USER_PROFILE,
    },
    {
      id: SectionNames.NOTIFICATION_SETTINGS,
      route: mobileRoutes.NOTIFICATION_SETTINGS,
    },
    {
      id: SectionNames.USERS,
      route: mobileRoutes.USERS_DIRECTORY,
    },
    {
      id: SectionNames.QR_CODES,
      route: mobileRoutes.QR_CODES,
    },

    // TODO: detect if the app is installed on the device. Do not show this section if it is.
    // Only add this section if the app is not running in a WebView
    ...(window.ReactNativeWebView
      ? []
      : [
          {
            id: SectionNames.ONE_TIME_POPUP,
            route: mobileRoutes.ONE_TIME_POPUP,
          },
        ]),
  ]

  public constructor(
    private readonly common: MobileCommonStore,
    private readonly eventsStore: MobileEventStore,
    private readonly state: MobileInitialState,
    private readonly projectsStore: ProjectsStore,
  ) {
    makeObservable(this)
  }

  @computed
  public get sidebarItems() {
    return this.sidebarSections.map(sidebarSection => ({
      ...sidebarSection,
      name: this.getSectionName(sidebarSection.id),
    }))
  }

  private getSectionName = (name: SectionNames) => {
    switch (name) {
      case SectionNames.QR_CODES:
        return Localization.translator.scanner
      case SectionNames.ADD_TEAMMATES:
        return Localization.translator.addTeammates
      case SectionNames.USERS:
        return Localization.translator.directory
      case SectionNames.PROFILE:
        return Localization.translator.profile
      case SectionNames.NOTIFICATION_SETTINGS:
        return Localization.translator.preferences
      case SectionNames.ONE_TIME_POPUP:
        return Localization.translator.getMobileNativeApp
    }
  }

  public onClickHandler = (isDisabled: boolean, route: string) => {
    if (isDisabled) {
      return
    }

    if (route === mobileRoutes.USER_PROFILE) {
      route = mobileRoutes.USER_PROFILE.replace(':userId', this.state.user.id)
    }

    this.common.toggleSidebar()

    if (route === mobileRoutes.ONE_TIME_POPUP) {
      return this.eventsStore.dispatch(SHOW_ONE_TIME_POPUP)
    }

    this.common._displayView(route)
  }

  public logoutHandler = () => {
    this.common.toggleSidebar()
    this.eventsStore.dispatch(LOGOUT)
  }

  public get isSidebarOpened() {
    return this.common.isSidebarOpen
  }

  public get activeProject() {
    return this.state.activeProject
  }

  @computed
  public get projects(): IProject[] {
    return this.projectsStore.list
  }

  public get areProjectMembersLoading() {
    return this.state.loading.get(GET_PROJECT_MEMBERS)
  }

  // Re-selecting the active project would dispatch HANDLE_SAME_PROJECT_SELECTION,
  // which force-displays the default page and navigates the user away.
  public selectProject = (project: IProject) => {
    // mobile's HANDLE_SAME_PROJECT_SELECTION force-navigates; desktop's is a no-op
    if (project.id === this.activeProject.id) {
      return
    }

    this.common.toggleSidebar()
    this.projectsStore.selectProject(project.id)
  }
}
